import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, KeyRound, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { formatPct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmptyRow, LoadingRow } from "@/components/common/States";
import UserDialog from "@/components/users/UserDialog";
import TempPasswordResult from "@/components/users/TempPasswordResult";
import DeletionRequests from "@/components/users/DeletionRequests";

function UserRow({ u, showBonus, canEdit, canDelete, onEdit, onDelete, canTemp, selected, onToggle, onTemp, onRevoke }) {
  return (
    <tr data-testid={`user-row-${u.email}`}>
      {canTemp && <td><input type="checkbox" checked={selected} onChange={() => onToggle(u.id)} data-testid={`user-select-${u.email}`} /></td>}
      <td className="font-medium text-slate-900">{u.name}<div className="text-[11px] text-slate-400">{u.title}</div></td>
      <td className="font-mono text-xs">{u.email}</td>
      <td><span className="text-[11px] px-1.5 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200" data-testid={`user-role-${u.email}`}>{u.role_name}</span></td>
      <td className="font-mono text-xs">{u.phone || "—"}</td>
      <td className="text-[11px] text-slate-600" data-testid={`user-scope-${u.email}`}>
        Cari: <b>{u.effective_scope?.customers === "own" ? "Kendi" : "Tümü"}</b> · Satış: <b>{u.effective_scope?.sales === "own" ? "Kendi" : "Tümü"}</b>
      </td>
      {showBonus && <td className="text-right font-mono" data-testid={`user-bonus-${u.email}`}>{u.bonus_rate != null ? formatPct(u.bonus_rate, 2) : "—"}</td>}
      <td>
        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${u.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
          {u.is_active ? "Aktif" : "Pasif"}
        </span>
      </td>
      <td className="text-right whitespace-nowrap">
        {canTemp && <Button variant="ghost" size="sm" className="h-7 text-[12px] text-blue-700" onClick={() => onTemp([u.id])} data-testid={`user-temp-pw-${u.email}`}><KeyRound className="h-3.5 w-3.5 mr-1" />Geçici Şifre Gönder</Button>}
        {onRevoke && <Button variant="ghost" size="sm" className="h-7 text-[12px] text-rose-700" onClick={() => onRevoke(u)} data-testid={`user-revoke-sessions-${u.email}`}><LogOut className="h-3.5 w-3.5 mr-1" />Oturumları Sonlandır</Button>}
        {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(u)} data-testid={`user-edit-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        {canDelete && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => onDelete(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </td>
    </tr>
  );
}

export default function UsersTab() {
  const { can, flag, user } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sel, setSel] = useState([]);
  const [confirmIds, setConfirmIds] = useState(null);
  const [tempRes, setTempRes] = useState(null);
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [allOpen, setAllOpen] = useState(false);
  const [allPw, setAllPw] = useState("");
  const revokeAll = async () => {
    try { const { data } = await api.post("/users/sessions/revoke-all", { password: allPw }); toast.success(`${data.revoked} oturum kapatıldı (sizin oturumunuz açık)`); setAllOpen(false); }
    catch (e) { toast.error(apiError(e)); }
    setAllPw("");
  };
  const revoke = async () => {
    try { const { data } = await api.post(`/users/${revoking.id}/sessions/revoke`); toast.success(`${revoking.name}: ${data.revoked} oturum sonlandırıldı`); }
    catch (e) { toast.error(apiError(e)); }
    setRevoking(null);
  };
  const canTemp = !!(user?.is_super || user?.role_key === "yonetici");
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const sendTemp = async () => {
    setSending(true);
    try {
      const ids = confirmIds;
      const { data } = ids.length === 1 ? await api.post(`/users/${ids[0]}/temp-password`) : await api.post("/users/temp-password/bulk", { ids });
      setTempRes(data.results); setSel([]); load();
    } catch (e) { toast.error(apiError(e)); }
    setSending(false); setConfirmIds(null);
  };

  const load = useCallback(() => api.get("/users", { params: { q, page_size: 200 } }).then(({ data }) => setItems(data.items)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/roles").then(({ data }) => setRoles(data)); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleting.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) { toast.error(apiError(e)); }
    setDeleting(null);
  };

  const showBonus = flag("bonus_view");
  const cols = 7 + (showBonus ? 1 : 0) + (canTemp ? 1 : 0);
  return (
    <div className="panel overflow-hidden" data-testid="users-tab">
      <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-200">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-8 h-9" placeholder="Ad veya e-posta ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search-input" />
        </div>
        <div className="flex items-center gap-2">
          {canTemp && <Button size="sm" variant="outline" disabled={!sel.length} onClick={() => setConfirmIds(sel)} data-testid="user-bulk-temp-pw"><KeyRound className="h-4 w-4 mr-1" />Seçililere Geçici Şifre Gönder{sel.length ? ` (${sel.length})` : ""}</Button>}
          {user?.is_super && <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={() => setAllOpen(true)} data-testid="users-revoke-all-button"><LogOut className="h-4 w-4 mr-1" />Tüm Oturumları Kapat</Button>}
          {can("kullanicilar", "create") && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEditing({})} data-testid="user-create-button"><Plus className="h-4 w-4 mr-1" />Yeni Kullanıcı</Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        {user?.is_super && <DeletionRequests />}
        <table className="erp-table" data-testid="users-table">
          <thead><tr>{canTemp && <th><input type="checkbox" checked={!!items?.length && sel.length === items.length} onChange={(e) => setSel(e.target.checked ? items.map((u) => u.id) : [])} data-testid="user-select-all" /></th>}<th>Ad Soyad</th><th>E-posta</th><th>Rol</th><th>Telefon</th><th>Veri Kapsamı</th>{showBonus && <th className="text-right">Prim Oranı</th>}<th>Durum</th><th></th></tr></thead>
          <tbody>
            {!items ? <LoadingRow cols={cols} /> : items.length === 0 ? <EmptyRow cols={cols} /> : items.map((u) => (
              <UserRow key={u.id} u={u} showBonus={showBonus} canEdit={can("kullanicilar", "edit")} canDelete={can("kullanicilar", "delete")} onEdit={setEditing} onDelete={setDeleting}
                canTemp={canTemp} selected={sel.includes(u.id)} onToggle={toggle} onTemp={setConfirmIds} onRevoke={user?.is_super && u.id !== user.id ? setRevoking : null} />
            ))}
          </tbody>
        </table>
      </div>
      {editing && <UserDialog user={editing} roles={roles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {tempRes && <TempPasswordResult results={tempRes} onClose={() => setTempRes(null)} />}
      <AlertDialog open={!!confirmIds} onOpenChange={(o) => !o && !sending && setConfirmIds(null)}>
        <AlertDialogContent data-testid="temp-pw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmIds?.length} kullanıcıya geçici şifre gönderilsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Her kullanıcı için güçlü bir geçici şifre üretilir, mevcut şifresi geçersiz olur, hesap aktifleştirilir ve e-posta ile gönderilir. İlk girişte şifre değişikliği zorunludur; geçici şifre 72 saat geçerlidir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending} data-testid="temp-pw-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction disabled={sending} className="bg-blue-600 hover:bg-blue-700" onClick={(e) => { e.preventDefault(); sendTemp(); }} data-testid="temp-pw-confirm">{sending ? "Gönderiliyor…" : "Gönder"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>{deleting?.name} ({deleting?.email}) kalıcı olarak silinecek. Bu işlem denetim kaydına yazılır.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete} data-testid="user-delete-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={allOpen} onOpenChange={(o) => { if (!o) { setAllOpen(false); setAllPw(""); } }}>
        <AlertDialogContent data-testid="users-revoke-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tüm oturumlar kapatılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>Sizin bu oturumunuz hariç tüm kullanıcılar (web + mobil) çıkış yapar ve yeniden giriş yapmalıdır. Onay için şifrenizi girin.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input type="password" value={allPw} onChange={(e) => setAllPw(e.target.value)} placeholder="Şifreniz" autoComplete="current-password" data-testid="users-revoke-all-password" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="users-revoke-all-cancel">Vazgeç</AlertDialogCancel>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={!allPw} onClick={revokeAll} data-testid="users-revoke-all-confirm">Tüm Oturumları Kapat</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <AlertDialogContent data-testid="user-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Oturumlar sonlandırılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>{revoking?.name} ({revoking?.email}) tüm cihazlarda (web + mobil) oturumdan çıkarılır ve yeniden giriş yapması gerekir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-revoke-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={revoke} data-testid="user-revoke-confirm">Oturumları Sonlandır</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
